import Phaser from 'phaser';

interface ShipColors {
    hull: number;
    front: number;
    rear: number;
    wings: number;
    engines: number;
}

type ShipModelType = 'player' | 'red' | 'yellow' | 'green' | 'blue' | 'purple';

interface ShipOptions {
    scale?: number;
    lineColor?: number;
    lineAlpha?: number;
    lineWidth?: number;
    innerLineWidth?: number;
    innerLineAlpha?: number;
    colors?: Partial<ShipColors>;
    fillAlpha?: number;
    modelType?: ShipModelType;
}

type Point = [number, number];

interface ShipModel {
    silhouette: Point[];
    hullCore: Point[];
    wingL: Point[];
    wingR: Point[];
    engineL: Point[];
    engineR: Point[];
    frontPlate: Point[];
    rearPlate: Point[];
}

/**
 * Procedural, line-art ship with tintable regions.
 * Regions can be tinted to match fitted module colors.
 */
export class ShipSprite {
    private scene: Phaser.Scene;
    private opts: Required<Omit<ShipOptions, 'colors' | 'modelType'>> & { colors: ShipColors };
    private modelType: ShipModelType;
    public container: Phaser.GameObjects.Container;
    private model: ShipModel;
    private layers: {
        wingsL: Phaser.GameObjects.Graphics;
        wingsR: Phaser.GameObjects.Graphics;
        enginesL: Phaser.GameObjects.Graphics;
        enginesR: Phaser.GameObjects.Graphics;
        hull: Phaser.GameObjects.Graphics;
        rear: Phaser.GameObjects.Graphics;
        front: Phaser.GameObjects.Graphics;
        inner: Phaser.GameObjects.Graphics;
        outline: Phaser.GameObjects.Graphics;
    };

    constructor(scene: Phaser.Scene, x: number, y: number, opts: ShipOptions = {}) {
        this.scene = scene;
        this.modelType = opts.modelType ?? 'player';

        // Visual defaults
        this.opts = {
            scale: opts.scale ?? 1,
            lineColor: opts.lineColor ?? 0x4a4a6e,
            lineAlpha: opts.lineAlpha ?? 1,
            lineWidth: opts.lineWidth ?? 4,
            innerLineWidth: opts.innerLineWidth ?? 2,
            innerLineAlpha: opts.innerLineAlpha ?? 0.4,
            fillAlpha: opts.fillAlpha ?? 1,
            colors: {
                hull: 0x1a1a2e,
                front: 0x1e1e38,
                rear: 0x16162a,
                wings: 0x1c1c32,
                engines: 0x181830,
                ...opts.colors,
            },
        };

        this.container = scene.add.container(x, y);
        this.container.setScale(this.opts.scale);

        // Build geometry model based on type
        this.model = this.buildModel(this.modelType);

        // Graphics layers (one per region for easy tinting)
        // Order matters for layering
        this.layers = {
            wingsL: scene.add.graphics(),
            wingsR: scene.add.graphics(),
            enginesL: scene.add.graphics(),
            enginesR: scene.add.graphics(),
            hull: scene.add.graphics(),
            rear: scene.add.graphics(),
            front: scene.add.graphics(),
            inner: scene.add.graphics(),
            outline: scene.add.graphics(),
        };

        // Add in draw order: wings/engines fills, hullCore fill, inner lines, silhouette outline last
        this.container.add([
            this.layers.wingsL,
            this.layers.wingsR,
            this.layers.enginesL,
            this.layers.enginesR,
            this.layers.hull,
            this.layers.rear,
            this.layers.front,
            this.layers.inner,
            this.layers.outline,
        ]);

        this.drawAll();
    }

    destroy(): void {
        this.container.destroy(true);
    }

    setPosition(x: number, y: number): void {
        this.container.setPosition(x, y);
    }

    setScale(s: number): void {
        this.container.setScale(s);
    }

    /**
     * Tint a logical region.
     * @param regionName "front" | "rear" | "wings" | "engines" | "hull"
     * @param colorHex Color as hex number (0xRRGGBB)
     */
    setRegionTint(regionName: keyof ShipColors | 'wings' | 'engines', colorHex: number): void {
        const c = this.opts.colors;

        if (regionName === 'wings') {
            c.wings = colorHex;
            this.drawWings();
            return;
        }

        if (regionName === 'engines') {
            c.engines = colorHex;
            this.drawEngines();
            return;
        }

        if (regionName === 'hull') {
            c.hull = colorHex;
            this.drawHull();
        } else if (regionName === 'front') {
            c.front = colorHex;
            this.drawFront();
        } else if (regionName === 'rear') {
            c.rear = colorHex;
            this.drawRear();
        }
    }

    /**
     * Set multiple region colors at once
     */
    setPalette(partialColors: Partial<ShipColors>): void {
        Object.assign(this.opts.colors, partialColors);
        this.drawAll();
    }

    /**
     * Reset all colors to default dark theme
     */
    resetColors(): void {
        this.opts.colors = {
            hull: 0x1a1a2e,
            front: 0x1e1e38,
            rear: 0x16162a,
            wings: 0x1c1c32,
            engines: 0x181830,
        };
        this.drawAll();
    }

    // ----------------------------
    // Drawing
    // ----------------------------

    private drawAll(): void {
        // Fill order: wings/engines first, then hullCore, then plates
        this.drawWings();
        this.drawEngines();
        this.drawHull();
        this.drawRear();
        this.drawFront();
        // Lines last
        this.drawInnerLines();
        this.drawOutline();
    }

    private drawHull(): void {
        const g = this.layers.hull;
        g.clear();
        // Use hullCore (the central body) for the hull fill
        this.fillPoly(g, this.model.hullCore, this.opts.colors.hull, this.opts.fillAlpha);
    }

    private drawWings(): void {
        this.layers.wingsL.clear();
        this.layers.wingsR.clear();
        this.fillPoly(this.layers.wingsL, this.model.wingL, this.opts.colors.wings, this.opts.fillAlpha);
        this.fillPoly(this.layers.wingsR, this.model.wingR, this.opts.colors.wings, this.opts.fillAlpha);
    }

    private drawEngines(): void {
        this.layers.enginesL.clear();
        this.layers.enginesR.clear();
        this.fillPoly(this.layers.enginesL, this.model.engineL, this.opts.colors.engines, this.opts.fillAlpha);
        this.fillPoly(this.layers.enginesR, this.model.engineR, this.opts.colors.engines, this.opts.fillAlpha);
    }

    private drawFront(): void {
        const g = this.layers.front;
        g.clear();
        this.fillPoly(g, this.model.frontPlate, this.opts.colors.front, this.opts.fillAlpha);
    }

    private drawRear(): void {
        const g = this.layers.rear;
        g.clear();
        this.fillPoly(g, this.model.rearPlate, this.opts.colors.rear, this.opts.fillAlpha);
    }

    private drawOutline(): void {
        const g = this.layers.outline;
        g.clear();

        // Thick single outline: ONLY the silhouette
        g.lineStyle(this.opts.lineWidth, this.opts.lineColor, this.opts.lineAlpha);
        this.strokePoly(g, this.model.silhouette);

        // Optional: thin outline for hullCore border (subtle)
        g.lineStyle(Math.max(1, this.opts.innerLineWidth), this.opts.lineColor, 0.35);
        this.strokePoly(g, this.model.hullCore);

        // DO NOT stroke wings/engines with thick outline - they're inside the silhouette
    }

    private drawInnerLines(): void {
        const g = this.layers.inner;
        g.clear();

        // Only draw detailed inner lines for player ship - other ships are simpler
        if (this.modelType !== 'player') {
            // For enemy ships, just draw a simple center spine
            g.lineStyle(this.opts.innerLineWidth, this.opts.lineColor, this.opts.innerLineAlpha * 0.5);
            g.beginPath();
            g.moveTo(0, this.model.rearPlate[0][1] + 20);
            g.lineTo(0, this.model.frontPlate[3][1] - 20);
            g.strokePath();
            return;
        }

        g.lineStyle(this.opts.innerLineWidth, this.opts.lineColor, this.opts.innerLineAlpha);

        // Center spine
        g.beginPath();
        g.moveTo(0, -120);
        g.lineTo(0, 115);
        g.strokePath();

        // Shoulder panel line
        this.strokePolyline(g, [[-18, -95], [0, -110], [18, -95]]);

        // Mid body panel (hexagonal shape)
        this.strokePolyline(g, [
            [-16, -50], [-24, -10], [-20, 40], [0, 55], [20, 40], [24, -10], [16, -50], [-16, -50]
        ]);

        // Engine segment lines (right side)
        this.strokePolyline(g, [[36, -60], [60, -50]]);
        this.strokePolyline(g, [[40, 0], [65, 10]]);
        this.strokePolyline(g, [[38, 60], [58, 75]]);

        // Engine segment lines (left - mirrored)
        this.strokePolyline(g, this.mirrorX([[36, -60], [60, -50]]));
        this.strokePolyline(g, this.mirrorX([[40, 0], [65, 10]]));
        this.strokePolyline(g, this.mirrorX([[38, 60], [58, 75]]));

        // Wing inner edge lines
        this.strokePolyline(g, [[50, -5], [95, 25], [85, 65]]);
        this.strokePolyline(g, this.mirrorX([[50, -5], [95, 25], [85, 65]]));
    }

    // ----------------------------
    // Geometry
    // ----------------------------

    private buildModel(modelType: ShipModelType): ShipModel {
        switch (modelType) {
            case 'red': return this.buildModelRed();
            case 'yellow': return this.buildModelYellow();
            case 'green': return this.buildModelGreen();
            case 'blue': return this.buildModelBlue();
            case 'purple': return this.buildModelPurple();
            case 'player':
            default:
                return this.buildModelPlayer();
        }
    }

    private buildModelPlayer(): ShipModel {
        // Full ship silhouette (single-stroke outline).
        // Cleaned, symmetric version. Front is +Y (down), rear is -Y (up).
        const silhouetteR: Point[] = [
            [8, -141],
            [22, -134],
            [30, -106],
            [48, -92],
            [58, -46],
            [74, -22],
            [100, -20],
            [126, 46],
            [94, 68],
            [88, 86],
            [60, 90],
            [52, 84],
            [28, 118],
            [10, 126],
        ];
        const silhouette: Point[] = [
            ...silhouetteR.map(([x, y]): Point => [-x, y]).reverse(),
            ...silhouetteR
        ];

        // Central "body" (tintable hull core) - inside the silhouette
        const hullCoreR: Point[] = [
            [10, -132],
            [20, -124],
            [32, -108],
            [44, -78],
            [48, -30],
            [48, 36],
            [40, 76],
            [24, 110],
            [8, 126],
        ];
        const hullCore: Point[] = [
            ...hullCoreR.map(([x, y]): Point => [-x, y]).reverse(),
            ...hullCoreR
        ];

        // Wings (tintable) - shrink slightly so they fit inside the silhouette
        const wingRBase: Point[] = [
            [60, -10],
            [124, 44],
            [100, 66],
            [92, 86],
            [64, 88],
            [58, 56],
        ];
        const wingShrink = 0.88;
        const wingInsetX = -8;
        let wingR = this.scalePolyAboutCentroid(wingRBase, wingShrink);
        wingR = this.translatePoly(wingR, wingInsetX, 0);
        const wingL: Point[] = wingR.map(([x, y]): Point => [-x, y]);

        // Engines / side pods (tintable) - shrink to fit inside silhouette
        const engineRBase: Point[] = [
            [34, -118],
            [52, -108],
            [64, -86],
            [76, -20],
            [74, 64],
            [62, 108],
            [44, 100],
            [32, 34],
            [32, -28],
        ];
        const engineShrink = 0.92;
        const engineInsetX = -4;
        let engineR = this.scalePolyAboutCentroid(engineRBase, engineShrink);
        engineR = this.translatePoly(engineR, engineInsetX, 0);
        const engineL: Point[] = engineR.map(([x, y]): Point => [-x, y]);

        // Front "middle-front" plate (tintable)
        const frontPlate: Point[] = [
            [0, 56],
            [30, 70],
            [40, 98],
            [0, 122],
            [-40, 98],
            [-30, 70]
        ];

        // Rear/cockpit cap (tintable)
        const rearPlate: Point[] = [
            [0, -132],
            [16, -126],
            [24, -104],
            [0, -84],
            [-24, -104],
            [-16, -126]
        ];

        return {
            silhouette,
            hullCore,
            wingL,
            wingR,
            engineL,
            engineR,
            frontPlate,
            rearPlate
        };
    }

    // ============================================================
    // RED (Brawler) — bigger, broad shoulders, heavy wings/pods
    // ============================================================
    private buildModelRed(): ShipModel {
        const silhouetteR: Point[] = [
            [10, -190], [30, -182], [44, -150], [64, -130], [74, -70],
            [92, -40], [120, -38], [164, 40], [126, 70], [120, 96],
            [86, 102], [74, 92], [40, 140], [12, 152],
        ];
        const silhouette: Point[] = [
            ...silhouetteR.map(([x, y]): Point => [-x, y]).reverse(),
            ...silhouetteR
        ];

        const hullCoreR: Point[] = [
            [12, -172], [26, -162], [40, -140], [56, -104], [62, -52],
            [62, 40], [52, 96], [32, 134], [10, 150],
        ];
        const hullCore: Point[] = [
            ...hullCoreR.map(([x, y]): Point => [-x, y]).reverse(),
            ...hullCoreR
        ];

        const wingRBase: Point[] = [
            [78, -20], [162, 44], [132, 82], [118, 108], [86, 112], [74, 78],
        ];
        let wingR = this.scalePolyAboutCentroid(wingRBase, 0.90);
        wingR = this.translatePoly(wingR, -10, 0);
        const wingL: Point[] = wingR.map(([x, y]): Point => [-x, y]);

        const engineRBase: Point[] = [
            [48, -158], [72, -144], [88, -112], [104, -40], [102, 76],
            [84, 140], [60, 130], [44, 50], [44, -40],
        ];
        let engineR = this.scalePolyAboutCentroid(engineRBase, 0.93);
        engineR = this.translatePoly(engineR, -6, 0);
        const engineL: Point[] = engineR.map(([x, y]): Point => [-x, y]);

        const frontPlate: Point[] = [
            [0, 70], [42, 86], [54, 124], [0, 156], [-54, 124], [-42, 86],
        ];
        const rearPlate: Point[] = [
            [0, -172], [22, -164], [34, -140], [0, -114], [-34, -140], [-22, -164],
        ];

        return { silhouette, hullCore, wingL, wingR, engineL, engineR, frontPlate, rearPlate };
    }

    // ============================================================
    // YELLOW (Dart) — smaller, narrow, fast-looking, sharp nose
    // ============================================================
    private buildModelYellow(): ShipModel {
        const silhouetteR: Point[] = [
            [6, -100], [16, -96], [24, -76], [34, -64], [40, -30],
            [52, -18], [70, -16], [88, 30], [66, 44], [60, 60],
            [42, 64], [38, 58], [22, 84], [8, 110],
        ];
        const silhouette: Point[] = [
            ...silhouetteR.map(([x, y]): Point => [-x, y]).reverse(),
            ...silhouetteR
        ];

        const hullCoreR: Point[] = [
            [8, -92], [16, -86], [26, -74], [32, -52], [34, -18],
            [34, 30], [28, 60], [18, 86], [6, 106],
        ];
        const hullCore: Point[] = [
            ...hullCoreR.map(([x, y]): Point => [-x, y]).reverse(),
            ...hullCoreR
        ];

        const wingRBase: Point[] = [
            [46, 0], [82, 32], [68, 44], [62, 58], [46, 60], [42, 38],
        ];
        let wingR = this.scalePolyAboutCentroid(wingRBase, 0.92);
        wingR = this.translatePoly(wingR, -4, 0);
        const wingL: Point[] = wingR.map(([x, y]): Point => [-x, y]);

        const engineRBase: Point[] = [
            [26, -84], [38, -78], [46, -60], [56, -12], [54, 44],
            [44, 86], [32, 80], [24, 24], [24, -20],
        ];
        let engineR = this.scalePolyAboutCentroid(engineRBase, 0.94);
        engineR = this.translatePoly(engineR, -2, 0);
        const engineL: Point[] = engineR.map(([x, y]): Point => [-x, y]);

        const frontPlate: Point[] = [
            [0, 46], [20, 56], [28, 80], [0, 112], [-28, 80], [-20, 56],
        ];
        const rearPlate: Point[] = [
            [0, -92], [14, -88], [20, -74], [0, -60], [-20, -74], [-14, -88],
        ];

        return { silhouette, hullCore, wingL, wingR, engineL, engineR, frontPlate, rearPlate };
    }

    // ============================================================
    // GREEN (Hauler) — biggest, blockier, heavy "cargo" profile
    // ============================================================
    private buildModelGreen(): ShipModel {
        const silhouetteR: Point[] = [
            [12, -220], [36, -214], [52, -176], [78, -160], [92, -90],
            [110, -64], [140, -62], [190, 60], [150, 90], [140, 120],
            [98, 128], [88, 118], [54, 176], [14, 192],
        ];
        const silhouette: Point[] = [
            ...silhouetteR.map(([x, y]): Point => [-x, y]).reverse(),
            ...silhouetteR
        ];

        const hullCoreR: Point[] = [
            [14, -200], [34, -192], [50, -172], [68, -132], [74, -70],
            [74, 58], [62, 118], [38, 168], [12, 190],
        ];
        const hullCore: Point[] = [
            ...hullCoreR.map(([x, y]): Point => [-x, y]).reverse(),
            ...hullCoreR
        ];

        const wingRBase: Point[] = [
            [96, -10], [186, 64], [156, 96], [148, 128], [112, 132], [104, 98],
        ];
        let wingR = this.scalePolyAboutCentroid(wingRBase, 0.90);
        wingR = this.translatePoly(wingR, -12, 0);
        const wingL: Point[] = wingR.map(([x, y]): Point => [-x, y]);

        const engineRBase: Point[] = [
            [60, -186], [88, -172], [110, -140], [132, -60], [130, 98],
            [108, 174], [76, 162], [56, 68], [56, -54],
        ];
        let engineR = this.scalePolyAboutCentroid(engineRBase, 0.92);
        engineR = this.translatePoly(engineR, -8, 0);
        const engineL: Point[] = engineR.map(([x, y]): Point => [-x, y]);

        const frontPlate: Point[] = [
            [0, 82], [42, 98], [56, 132], [0, 186], [-56, 132], [-42, 98],
        ];
        const rearPlate: Point[] = [
            [0, -188], [22, -180], [36, -152], [0, -124], [-36, -152], [-22, -180],
        ];

        return { silhouette, hullCore, wingL, wingR, engineL, engineR, frontPlate, rearPlate };
    }

    // ============================================================
    // BLUE (Skater) — smaller length, wide forward-swept wings
    // ============================================================
    private buildModelBlue(): ShipModel {
        const silhouetteR: Point[] = [
            [8, -112], [22, -108], [32, -88], [50, -78], [60, -44],
            [78, -34], [106, -34], [128, 18], [98, 42], [92, 60],
            [66, 66], [58, 58], [34, 82], [12, 92],
        ];
        const silhouette: Point[] = [
            ...silhouetteR.map(([x, y]): Point => [-x, y]).reverse(),
            ...silhouetteR
        ];

        const hullCoreR: Point[] = [
            [10, -98], [22, -92], [34, -78], [42, -54], [44, -20],
            [44, 30], [36, 54], [22, 78], [8, 88],
        ];
        const hullCore: Point[] = [
            ...hullCoreR.map(([x, y]): Point => [-x, y]).reverse(),
            ...hullCoreR
        ];

        const wingRBase: Point[] = [
            [70, -12], [130, 8], [120, 34], [96, 58], [68, 60], [62, 34],
        ];
        let wingR = this.scalePolyAboutCentroid(wingRBase, 0.90);
        wingR = this.translatePoly(wingR, -6, 0);
        const wingL: Point[] = wingR.map(([x, y]): Point => [-x, y]);

        const engineRBase: Point[] = [
            [38, -92], [54, -84], [66, -66], [78, -24], [76, 48],
            [62, 86], [46, 80], [36, 26], [36, -20],
        ];
        let engineR = this.scalePolyAboutCentroid(engineRBase, 0.93);
        engineR = this.translatePoly(engineR, -4, 0);
        const engineL: Point[] = engineR.map(([x, y]): Point => [-x, y]);

        const frontPlate: Point[] = [
            [0, 50], [34, 56], [44, 72], [0, 94], [-44, 72], [-34, 56],
        ];
        const rearPlate: Point[] = [
            [0, -98], [18, -92], [26, -76], [0, -60], [-26, -76], [-18, -92],
        ];

        return { silhouette, hullCore, wingL, wingR, engineL, engineR, frontPlate, rearPlate };
    }

    // ============================================================
    // PURPLE (Lancer) — bigger but narrow/long, "spear" silhouette
    // ============================================================
    private buildModelPurple(): ShipModel {
        const silhouetteR: Point[] = [
            [6, -210], [18, -202], [26, -170], [34, -140], [38, -94],
            [48, -60], [70, -56], [92, 10], [76, 34], [70, 70],
            [52, 90], [46, 82], [30, 138], [8, 176],
        ];
        const silhouette: Point[] = [
            ...silhouetteR.map(([x, y]): Point => [-x, y]).reverse(),
            ...silhouetteR
        ];

        const hullCoreR: Point[] = [
            [8, -194], [18, -186], [28, -168], [34, -136], [36, -90],
            [36, 40], [30, 86], [20, 134], [6, 172],
        ];
        const hullCore: Point[] = [
            ...hullCoreR.map(([x, y]): Point => [-x, y]).reverse(),
            ...hullCoreR
        ];

        const wingRBase: Point[] = [
            [56, -36], [92, 0], [84, 22], [70, 40], [52, 42], [48, 18],
        ];
        let wingR = this.scalePolyAboutCentroid(wingRBase, 0.92);
        wingR = this.translatePoly(wingR, -4, 0);
        const wingL: Point[] = wingR.map(([x, y]): Point => [-x, y]);

        const engineRBase: Point[] = [
            [28, -172], [42, -160], [52, -132], [62, -70], [60, 56],
            [50, 120], [38, 114], [28, 40], [28, -56],
        ];
        let engineR = this.scalePolyAboutCentroid(engineRBase, 0.93);
        engineR = this.translatePoly(engineR, -2, 0);
        const engineL: Point[] = engineR.map(([x, y]): Point => [-x, y]);

        const frontPlate: Point[] = [
            [0, 86], [28, 98], [36, 132], [0, 178], [-36, 132], [-28, 98],
        ];
        const rearPlate: Point[] = [
            [0, -202], [12, -196], [20, -176], [0, -156], [-20, -176], [-12, -196],
        ];

        return { silhouette, hullCore, wingL, wingR, engineL, engineR, frontPlate, rearPlate };
    }

    // ----------------------------
    // Low-level helpers
    // ----------------------------

    private fillPoly(g: Phaser.GameObjects.Graphics, pts: Point[], color: number, alpha: number = 1): void {
        g.fillStyle(color, alpha);
        g.beginPath();
        g.moveTo(pts[0][0], pts[0][1]);
        for (let i = 1; i < pts.length; i++) {
            g.lineTo(pts[i][0], pts[i][1]);
        }
        g.closePath();
        g.fillPath();
    }

    private strokePoly(g: Phaser.GameObjects.Graphics, pts: Point[]): void {
        g.beginPath();
        g.moveTo(pts[0][0], pts[0][1]);
        for (let i = 1; i < pts.length; i++) {
            g.lineTo(pts[i][0], pts[i][1]);
        }
        g.closePath();
        g.strokePath();
    }

    private strokePolyline(g: Phaser.GameObjects.Graphics, pts: Point[]): void {
        if (pts.length < 2) return;
        g.beginPath();
        g.moveTo(pts[0][0], pts[0][1]);
        for (let i = 1; i < pts.length; i++) {
            g.lineTo(pts[i][0], pts[i][1]);
        }
        g.strokePath();
    }

    private mirrorX(pts: Point[]): Point[] {
        return pts.map(([x, y]): Point => [-x, y]);
    }

    private scalePolyAboutCentroid(pts: Point[], s: number): Point[] {
        const cx = pts.reduce((a, p) => a + p[0], 0) / pts.length;
        const cy = pts.reduce((a, p) => a + p[1], 0) / pts.length;
        return pts.map(([x, y]): Point => [cx + (x - cx) * s, cy + (y - cy) * s]);
    }

    private translatePoly(pts: Point[], dx: number, dy: number): Point[] {
        return pts.map(([x, y]): Point => [x + dx, y + dy]);
    }

    /**
     * Get the ship model type for an enemy based on its primary aspect
     * @param aspects Array of element types (first one is primary)
     * @returns The appropriate ship model type
     */
    static getModelForAspect(aspects: string[]): ShipModelType {
        if (!aspects || aspects.length === 0) return 'red'; // Default

        const primary = aspects[0];
        switch (primary) {
            case 'THERMAL': return 'red';
            case 'EMP': return 'yellow';
            case 'RADIATION': return 'green';
            case 'CRYO': return 'blue';
            case 'VOID': return 'purple';
            default: return 'red';
        }
    }
}

export type { ShipModelType };
